import { useEffect, useState } from 'react';
import { api } from '../api/client';

/** A user's Read/Write/Delete rights on ONE form (its NavigationItem). Read is
 *  always true; Write gates create/edit/save/submit, Delete gates delete. */
export interface NavPermission {
  canRead: boolean;
  canWrite: boolean;
  canDelete: boolean;
  isAdmin: boolean;
  /** False until the permission has resolved (starts fail-closed). */
  ready: boolean;
}

const DENY: NavPermission = { canRead: true, canWrite: false, canDelete: false, isAdmin: false, ready: false };

/**
 * Resolves the current user's rights on a form by its `NavigationItem.NavigationId`
 * (e.g. `'Result Entry'`, `'Result Validation'`, `'Result Approval'`) via the shared
 * `GET /api/v1/permissions/nav?navId=…` endpoint. Every form gates its OWN actions
 * (Save/Submit/Delete, Project Notes / Comment) by its corresponding permission —
 * the same per-form model ALPACA uses. Fail-closed: Write/Delete stay false until the
 * call resolves and on any error, so buttons never flash enabled.
 *
 *   const perm = useNavPermission('Result Entry');
 *   <button disabled={!perm.canWrite}>Save</button>
 *   <NotesListPopup readOnly={!perm.canWrite} … />
 */
export function useNavPermission(navId: string): NavPermission {
  const [perm, setPerm] = useState<NavPermission>(DENY);

  useEffect(() => {
    let alive = true;
    setPerm(DENY);
    api.get('/api/v1/permissions/nav', { params: { navId }, silentLoading: true } as never)
      .then(r => {
        if (!alive) return;
        const d = (r.data as { data?: Partial<NavPermission> }).data ?? {};
        setPerm({
          canRead:   d.canRead   ?? true,
          canWrite:  !!d.canWrite,
          canDelete: !!d.canDelete,
          isAdmin:   !!d.isAdmin,
          ready:     true,
        });
      })
      .catch(() => { if (alive) setPerm({ ...DENY, ready: true }); });
    return () => { alive = false; };
  }, [navId]);

  return perm;
}
